import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { useResponsive } from '../hooks/useResponsive';

const BugReportModal = ({ onClose }) => {
  const { profile, realProfile, isImpersonating } = useAuth();
  const { isDesktop } = useResponsive();

  const [description, setDescription] = useState('');
  const [stepsToReproduce, setStepsToReproduce] = useState('');
  const [expectedBehavior, setExpectedBehavior] = useState('');
  const [actualBehavior, setActualBehavior] = useState('');
  const [screenshots, setScreenshots] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Capture browser info
  const getBrowserInfo = () => {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
    };
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter((file) => {
      // Only allow images up to 5MB
      if (!file.type.startsWith('image/')) {
        setError('Only image files are allowed');
        return false;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('Screenshots must be under 5MB');
        return false;
      }
      return true;
    });

    if (validFiles.length > 0) {
      setScreenshots((prev) => [...prev, ...validFiles].slice(0, 3)); // Max 3 screenshots
      setError('');
    }
  };

  const removeScreenshot = (index) => {
    setScreenshots((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      if (!description.trim()) {
        throw new Error('Please provide a bug description');
      }

      // Upload screenshots to storage
      const screenshotUrls = [];
      for (const file of screenshots) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${profile.id}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
          .from('bug-screenshots')
          .upload(fileName, file);

        if (uploadError) throw uploadError;

        // Generate a signed URL (valid for 1 year) for private bucket access
        const { data: signedUrlData, error: signedUrlError } = await supabase.storage
          .from('bug-screenshots')
          .createSignedUrl(fileName, 31536000); // 1 year in seconds

        if (signedUrlError) throw signedUrlError;

        screenshotUrls.push(signedUrlData.signedUrl);
      }

      // Get browser info
      const browserInfo = getBrowserInfo();

      // Submit bug report
      const bugReport = {
        user_id: profile?.id,
        user_email: profile?.email,
        user_role: profile?.role,
        organization_id: profile?.organization_id,
        page_url: window.location.href,
        browser_info: JSON.stringify(browserInfo),
        description: description.trim(),
        steps_to_reproduce: stepsToReproduce.trim() || null,
        expected_behavior: expectedBehavior.trim() || null,
        actual_behavior: actualBehavior.trim() || null,
        screenshot_urls: screenshotUrls.length > 0 ? screenshotUrls : null,
      };

      // Add impersonation context if applicable
      if (isImpersonating && realProfile) {
        bugReport.description = `[IMPERSONATION CONTEXT]\nReported by: ${realProfile.full_name || realProfile.email} (${realProfile.role})\nViewing as: ${profile.full_name || profile.email} (${profile.role})\n\n${bugReport.description}`;
      }

      const { error: insertError } = await supabase.from('bug_reports').insert(bugReport);

      if (insertError) throw insertError;

      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Error submitting bug report:', err);
      setError(err.message || 'Failed to submit bug report');
    } finally {
      setSubmitting(false);
    }
  };

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isDesktop ? '20px' : '0',
    },
    modal: {
      backgroundColor: 'white',
      borderRadius: isDesktop ? '8px' : '0',
      width: isDesktop ? '600px' : '100%',
      maxWidth: isDesktop ? '90vw' : '100%',
      maxHeight: isDesktop ? '90vh' : '100vh',
      overflow: 'auto',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      position: isDesktop ? 'relative' : 'fixed',
      bottom: isDesktop ? 'auto' : 0,
      boxSizing: 'border-box',
    },
    header: {
      padding: '20px',
      borderBottom: '1px solid #e0e0e0',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    title: {
      margin: 0,
      fontSize: '20px',
      fontWeight: 'bold',
      color: '#333',
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '24px',
      cursor: 'pointer',
      color: '#666',
      padding: '0',
      minHeight: '44px',
      minWidth: '44px',
    },
    content: {
      padding: '20px',
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: '500',
      color: '#333',
    },
    required: {
      color: '#dc3545',
    },
    input: {
      width: '100%',
      padding: '12px',
      border: '1px solid #e0e0e0',
      borderRadius: '4px',
      fontSize: '14px',
      boxSizing: 'border-box',
    },
    textarea: {
      width: '100%',
      padding: '12px',
      border: '1px solid #e0e0e0',
      borderRadius: '4px',
      fontSize: '14px',
      minHeight: '100px',
      resize: 'vertical',
      fontFamily: 'inherit',
      boxSizing: 'border-box',
    },
    fileInput: {
      display: 'none',
    },
    uploadButton: {
      padding: '12px 20px',
      backgroundColor: '#6c757d',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      minHeight: '44px',
    },
    screenshotList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      marginTop: '8px',
    },
    screenshotItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '8px',
      backgroundColor: '#f8f9fa',
      borderRadius: '4px',
    },
    removeButton: {
      padding: '4px 12px',
      backgroundColor: '#dc3545',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '12px',
      minHeight: '32px',
    },
    hint: {
      fontSize: '12px',
      color: '#666',
      marginTop: '4px',
    },
    error: {
      padding: '12px',
      backgroundColor: '#f8d7da',
      color: '#721c24',
      borderRadius: '4px',
      fontSize: '14px',
    },
    success: {
      padding: '12px',
      backgroundColor: '#d4edda',
      color: '#155724',
      borderRadius: '4px',
      fontSize: '14px',
    },
    footer: {
      padding: '20px',
      borderTop: '1px solid #e0e0e0',
      display: 'flex',
      gap: '10px',
      justifyContent: 'flex-end',
    },
    button: {
      padding: '12px 24px',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      minHeight: '44px',
      minWidth: '100px',
    },
    submitButton: {
      backgroundColor: '#007bff',
      color: 'white',
    },
    cancelButton: {
      backgroundColor: '#6c757d',
      color: 'white',
    },
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>Report a Bug</h2>
          <button style={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <div style={styles.content}>
          {error && <div style={styles.error}>{error}</div>}
          {success && (
            <div style={styles.success}>Bug report submitted successfully! Thank you.</div>
          )}

          <form style={styles.form} onSubmit={handleSubmit}>
            <div>
              <label style={styles.label}>
                Bug Description <span style={styles.required}>*</span>
              </label>
              <textarea
                style={styles.textarea}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Briefly describe the bug you encountered..."
                required
              />
            </div>

            <div>
              <label style={styles.label}>Steps to Reproduce (Optional)</label>
              <textarea
                style={styles.textarea}
                value={stepsToReproduce}
                onChange={(e) => setStepsToReproduce(e.target.value)}
                placeholder="1. Go to...&#10;2. Click on...&#10;3. See error..."
              />
            </div>

            <div>
              <label style={styles.label}>Expected Behavior (Optional)</label>
              <input
                style={styles.input}
                type="text"
                value={expectedBehavior}
                onChange={(e) => setExpectedBehavior(e.target.value)}
                placeholder="What did you expect to happen?"
              />
            </div>

            <div>
              <label style={styles.label}>Actual Behavior (Optional)</label>
              <input
                style={styles.input}
                type="text"
                value={actualBehavior}
                onChange={(e) => setActualBehavior(e.target.value)}
                placeholder="What actually happened?"
              />
            </div>

            <div>
              <label style={styles.label}>Screenshots (Optional, max 3)</label>
              <input
                type="file"
                id="screenshot-upload"
                style={styles.fileInput}
                accept="image/*"
                multiple
                onChange={handleFileChange}
              />
              <label htmlFor="screenshot-upload">
                <button
                  type="button"
                  style={styles.uploadButton}
                  onClick={() => document.getElementById('screenshot-upload').click()}
                  disabled={screenshots.length >= 3}
                >
                  {screenshots.length >= 3 ? 'Max 3 Screenshots' : 'Add Screenshots'}
                </button>
              </label>
              <div style={styles.hint}>
                PNG, JPG, or GIF. Max 5MB per file. ({screenshots.length}/3)
              </div>

              {screenshots.length > 0 && (
                <div style={styles.screenshotList}>
                  {screenshots.map((file, index) => (
                    <div key={index} style={styles.screenshotItem}>
                      <span>{file.name}</span>
                      <button
                        type="button"
                        style={styles.removeButton}
                        onClick={() => removeScreenshot(index)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </form>
        </div>

        <div style={styles.footer}>
          <button
            style={{ ...styles.button, ...styles.cancelButton }}
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            style={{ ...styles.button, ...styles.submitButton }}
            onClick={handleSubmit}
            disabled={submitting || success}
          >
            {submitting ? 'Submitting...' : 'Submit Report'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BugReportModal;
